import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AtSign,
  Inbox,
  Mail,
  MessageSquare,
  MessagesSquare,
  RefreshCw,
  Send,
} from 'lucide-react';
import { SOCIAL_PLATFORM_ORDER, SocialPlatform, type SocialConnection } from '@mg/shared';
import { api, ApiRequestError } from '../lib/api';
import { cn } from '../lib/cn';
import { formatRelative } from '../lib/display';
import { useTenant } from '../auth/TenantContext';
import { useTenantKey } from '../lib/useTenantKey';
import { PageHeader } from '../components/shell/PageHeader';
import { InboxSetupAlerts } from '../features/messages/InboxSetupAlerts';
import { replyBlockedReason } from '../features/messages/replyRules';
import { Badge, type BadgeTone } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Field';
import { ErrorState, LoadingState } from '../components/ui/States';
import { PLATFORM_META } from '../features/social/platforms';
import {
  accountCounts,
  CONVERSATION_TYPE_LABEL,
  filterConversations,
  folderCounts,
  platformCounts,
} from '../features/messages/seed-data';
import type {
  Conversation,
  ConversationType,
  InboxFolder,
  InboxMessage,
  InboxSelection,
  PlatformFilter,
} from '../features/messages/types';

const FOLDERS: { id: InboxFolder; label: string; icon: typeof Inbox }[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'unread', label: 'Unread', icon: Mail },
  { id: 'mentions', label: 'Mentions', icon: AtSign },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'dms', label: 'Direct Messages', icon: MessagesSquare },
];

const TYPE_TONE: Record<ConversationType, BadgeTone> = {
  dm: 'brand',
  comment: 'blue',
  mention: 'amber',
};

/**
 * Platforms whose API exposes a readable inbox (DMs, comments, and/or mentions):
 * Facebook + Instagram (Messenger DMs + post comments/mentions), X (mentions now;
 * DMs need dm.read + a paid X API tier), Threads (replies + mentions — Threads
 * has no direct-message API), and LinkedIn (Page comments + mentions, read-only —
 * LinkedIn offers apps no direct messages). YouTube has no inbox, so it
 * renders "N/A" when connected, so an empty tab isn't mistaken for a bug.
 */
const INBOX_PLATFORMS = new Set<SocialPlatform>([
  SocialPlatform.FACEBOOK,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.X,
  SocialPlatform.THREADS,
  SocialPlatform.LINKEDIN,
]);

/**
 * Messages — a consolidated social inbox (Outlook-style folders + message list
 * crossed with a Teams-style chat thread). Conversations are the REAL DMs from
 * connected accounts (Facebook Page Messenger today, via GET /messages/
 * conversations); replies post through the platform. Renders full-height under
 * the app shell and owns its own scroll regions.
 */
export function MessagesPage() {
  const { activeTenant } = useTenant();
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const workspaceName = activeTenant?.name ?? 'Workspace';

  const queryKey = useMemo(() => ['inbox', tenantKey] as const, [tenantKey]);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: ({ signal }) => api.getInboxConversations(signal),
    enabled: tenantKey !== '',
    staleTime: 60 * 1000,
  });

  const conversations = useMemo(() => data ?? [], [data]);

  // Which platforms are connected — so the inbox shows even with 0 messages, and
  // the platform rail can gray out platforms that aren't connected.
  const connectionsQuery = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
  });
  // Connected accounts grouped by platform — drives the per-account lines under each
  // bucket, and (via its keys) which platforms count as connected.
  const connectionsByPlatform = useMemo(() => {
    const map = new Map<SocialPlatform, SocialConnection[]>();
    for (const c of connectionsQuery.data ?? []) {
      if (!c.connected) continue;
      map.set(c.platform, [...(map.get(c.platform) ?? []), c]);
    }
    return map;
  }, [connectionsQuery.data]);
  // Platforms that have at least one conversation in the inbox. Every conversation
  // comes from a connected account, so this normally matches the connected set; any
  // platform with conversations still counts as "present" in the rail, so a thread is
  // never shown under a grayed-out tab (or hidden behind the empty state) if the two
  // lists ever disagree.
  const platformsWithConversations = useMemo(() => {
    const set = new Set<SocialPlatform>();
    for (const c of conversations) set.add(c.platform);
    return set;
  }, [conversations]);
  const connectedPlatforms = useMemo(
    () =>
      new Set<SocialPlatform>([
        ...connectionsByPlatform.keys(),
        ...platformsWithConversations,
      ]),
    [connectionsByPlatform, platformsWithConversations],
  );
  const hasAnyConnection = connectedPlatforms.size > 0;
  const loading = isLoading || connectionsQuery.isLoading;

  // Deep-link support: `/messages?c=<conversationId>` (e.g. from the Dashboard Messages
  // widget) preselects that conversation on mount. It stays selected as long as it's in
  // the visible list; the reconcile effect below clears it if the id is stale/unknown.
  const [searchParams] = useSearchParams();
  const [folder, setFolder] = useState<InboxFolder>('inbox');
  const [selection, setSelection] = useState<InboxSelection>({ kind: 'all' });
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('c'));
  const [sendError, setSendError] = useState<string | null>(null);

  const folderTotals = useMemo(() => folderCounts(conversations), [conversations]);
  const platformTotals = useMemo(() => platformCounts(conversations), [conversations]);
  const accountTotals = useMemo(() => accountCounts(conversations), [conversations]);
  const visible = useMemo(
    () => filterConversations(conversations, folder, selection),
    [conversations, folder, selection],
  );

  // Keep the selection valid when the visible list changes.
  useEffect(() => {
    if (selectedId && !visible.some((c) => c.id === selectedId)) {
      setSelectedId(null);
    }
  }, [visible, selectedId]);

  const selected = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId],
  );

  const patchCache = (fn: (list: Conversation[]) => Conversation[]) =>
    queryClient.setQueryData<Conversation[]>(queryKey, (prev) => fn(prev ?? []));

  /** Select a row, clear its unread flag, and sync "seen" back to the platform. */
  const handleSelect = (id: string) => {
    setSelectedId(id);
    setSendError(null);
    patchCache((list) => list.map((c) => (c.id === id && c.unread ? { ...c, unread: false } : c)));
    // Best-effort read-sync to Facebook/Instagram (badge already cleared locally).
    void api.markConversationSeen(id).catch(() => {});
  };

  const reply = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      api.replyToConversation(id, text),
    onMutate: ({ id, text }) => {
      setSendError(null);
      const optimisticId = `tmp-${Date.now()}`;
      const message: InboxMessage = {
        id: optimisticId,
        author: 'me',
        text,
        sentAt: new Date().toISOString(),
      };
      patchCache((list) =>
        list.map((c) => (c.id === id ? { ...c, messages: [...c.messages, message] } : c)),
      );
      return { id, optimisticId };
    },
    onError: (err, _vars, ctx) => {
      // Roll back the optimistic bubble and surface the reason.
      if (ctx) {
        patchCache((list) =>
          list.map((c) =>
            c.id === ctx.id
              ? { ...c, messages: c.messages.filter((m) => m.id !== ctx.optimisticId) }
              : c,
          ),
        );
      }
      setSendError(err instanceof ApiRequestError ? err.message : 'Could not send the message.');
    },
  });

  const handleSend = (text: string) => {
    if (selectedId) reply.mutate({ id: selectedId, text });
  };

  return (
    // The same full-width scroll region as StandardLayout, with the same room for its scrollbar
    // (an empty gutter here, as wide as the track StandardLayout always draws), so
    // this page's column is centered exactly where every other page's is. The column fills it
    // exactly and the inbox scrolls inside its own panes; the region scrolls only in a window too
    // short for the inbox's floor below, which <main> would otherwise cut off.
    <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[1440px] flex-col">
        {/* The title and the notices may give up height - the notices then scroll inside their own
            box (InboxSetupAlerts) - so however many notices there are, the inbox keeps at least
            20rem (its container's 21.25rem less the 1.25rem padding under it: 320px of 340px at
            full size). */}
        <div className="flex min-h-0 flex-col px-6 pt-6">
          <PageHeader
            title="Messages"
            icon={MessagesSquare}
            description={`${workspaceName} · Unified social inbox — DMs from your connected accounts`}
            actions={
              <Button variant="secondary" loading={isFetching} onClick={() => void refetch()}>
                {!isFetching && <RefreshCw className="h-4 w-4" aria-hidden />} Refresh
              </Button>
            }
          />
          <InboxSetupAlerts variant="messages" />
        </div>

        <div className="min-h-[18.75rem] flex-1 px-6 pb-5 sm:min-h-[21.25rem]">
          {loading ? (
            <div className="flex h-full items-center justify-center rounded-xl border border-line bg-card shadow-card">
              <LoadingState label="Loading your inbox…" />
            </div>
          ) : isError ? (
            <div className="flex h-full items-center justify-center rounded-xl border border-line bg-card shadow-card">
              <ErrorState
                message={error instanceof ApiRequestError ? error.message : 'Could not load messages.'}
                onRetry={() => void refetch()}
              />
            </div>
          ) : !hasAnyConnection ? (
            <EmptyInbox />
          ) : (
            <div className="flex h-full min-h-0 overflow-hidden rounded-xl border border-line bg-card shadow-card">
              <LeftRail
                folder={folder}
                selection={selection}
                folderTotals={folderTotals}
                platformTotals={platformTotals}
                accountTotals={accountTotals}
                connectedPlatforms={connectedPlatforms}
                connectionsByPlatform={connectionsByPlatform}
                onFolder={setFolder}
                onSelect={setSelection}
              />
              <ConversationList
                conversations={visible}
                selectedId={selectedId}
                showAccount={selection.kind !== 'account'}
                onSelect={handleSelect}
              />
              <ThreadPane
                conversation={selected}
                onSend={handleSend}
                sending={reply.isPending}
                sendError={sendError}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyInbox() {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-xl border border-line bg-card px-6 text-center shadow-card">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-chip">
        <MessagesSquare className="h-6 w-6 text-ink-faint" aria-hidden />
      </div>
      <p className="mt-3 text-sm font-semibold text-ink">No messages yet</p>
      <p className="mt-1 max-w-sm text-sm text-ink-muted">
        Direct messages from your connected accounts land here. If you connected Facebook before
        enabling messaging, reconnect it to grant message access — then conversations will appear.
      </p>
      <Link to="/connections" className="mt-4">
        <Button variant="secondary">Manage connections</Button>
      </Link>
    </div>
  );
}

/**
 * Avatar with an initials fallback when no image URL is available. `size` is px at full size; it is
 * drawn in rem, so it scales with the page when index.css scales the UI to 80%.
 */
function Avatar({ name, url, size }: { name: string; url?: string; size: number }) {
  const side = `${size / 16}rem`;
  if (url) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className="shrink-0 rounded-full object-cover"
        style={{ width: side, height: side }}
      />
    );
  }
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-chip font-semibold text-ink-muted"
      style={{ width: side, height: side, fontSize: `${(size * 0.4) / 16}rem` }}
      aria-hidden="true"
    >
      {(name.trim().charAt(0) || '?').toUpperCase()}
    </div>
  );
}

function LeftRail({
  folder,
  selection,
  folderTotals,
  platformTotals,
  accountTotals,
  connectedPlatforms,
  connectionsByPlatform,
  onFolder,
  onSelect,
}: {
  folder: InboxFolder;
  selection: InboxSelection;
  folderTotals: Record<InboxFolder, number>;
  platformTotals: Record<PlatformFilter, number>;
  accountTotals: Record<string, number>;
  connectedPlatforms: Set<SocialPlatform>;
  connectionsByPlatform: Map<SocialPlatform, SocialConnection[]>;
  onFolder: (folder: InboxFolder) => void;
  onSelect: (selection: InboxSelection) => void;
}) {
  return (
    <aside className="flex w-[13.75rem] shrink-0 flex-col overflow-y-auto border-r border-line-soft bg-page/50">
      <nav className="p-3" aria-label="Inbox folders">
        <ul className="space-y-0.5">
          {FOLDERS.map(({ id, label, icon: Icon }) => {
            const active = folder === id;
            const count = folderTotals[id] ?? 0;
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => onFolder(id)}
                  aria-current={active ? 'true' : undefined}
                  className={cn(
                    'focus-ring flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
                    active ? 'bg-tint font-medium text-tint-fg' : 'text-ink-strong hover:bg-card',
                  )}
                >
                  <Icon
                    className={cn('h-4 w-4 shrink-0', active ? 'text-tint-fg' : 'text-ink-faint')}
                    aria-hidden="true"
                  />
                  <span className="flex-1 truncate">{label}</span>
                  {count > 0 && (
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums',
                        // On the active row's tint, a step off it, as the count stood out before.
                        active ? 'bg-line text-tint-fg' : 'bg-chip text-ink-muted',
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-1 px-3 pb-4">
        <p className="mb-1.5 px-1 text-xs font-medium uppercase tracking-wide text-ink-faint">
          Platforms
        </p>
        <ul className="space-y-0.5">
          <PlatformRow
            label="All platforms"
            active={selection.kind === 'all'}
            count={platformTotals.all ?? 0}
            onClick={() => onSelect({ kind: 'all' })}
          />
          {SOCIAL_PLATFORM_ORDER.map((p) => {
            const meta = PLATFORM_META[p];
            return (
              <PlatformRow
                key={p}
                label={meta.label}
                icon={<meta.Icon className="h-4 w-4" />}
                active={selection.kind === 'platform' && selection.platform === p}
                count={platformTotals[p] ?? 0}
                connected={connectedPlatforms.has(p)}
                dmCapable={INBOX_PLATFORMS.has(p)}
                connections={connectionsByPlatform.get(p) ?? []}
                platformKey={p}
                selection={selection}
                accountTotals={accountTotals}
                onClick={() => onSelect({ kind: 'platform', platform: p })}
                onSelect={onSelect}
              />
            );
          })}
        </ul>
        <p className="mt-2 px-1 text-[0.6875rem] leading-snug text-ink-faint">
          Shows DMs, comments &amp; mentions where the platform&apos;s API exposes them.
        </p>
      </div>
    </aside>
  );
}

function PlatformRow({
  label,
  icon,
  active,
  count,
  connected = true,
  dmCapable = true,
  connections,
  platformKey,
  selection,
  accountTotals,
  onClick,
  onSelect,
}: {
  label: string;
  icon?: React.ReactNode;
  active: boolean;
  count: number;
  connected?: boolean;
  dmCapable?: boolean;
  connections?: SocialConnection[];
  platformKey?: SocialPlatform;
  selection?: InboxSelection;
  accountTotals?: Record<string, number>;
  onClick: () => void;
  onSelect?: (selection: InboxSelection) => void;
}) {
  // Not-connected platforms render grayed out and non-interactive so it's clear
  // which accounts are actually connected.
  if (!connected) {
    return (
      <li>
        <div
          title="Not connected"
          className="flex w-full cursor-default items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm text-ink-faint opacity-70"
        >
          <span className="flex h-4 w-4 shrink-0 items-center justify-center text-ink-ghost" aria-hidden="true">
            {icon}
          </span>
          <span className="flex-1 truncate">{label}</span>
          <span className="shrink-0 text-[0.625rem] font-medium uppercase tracking-wide text-ink-ghost">
            Not connected
          </span>
        </div>
      </li>
    );
  }

  // Connected, but the platform's API doesn't expose a readable message inbox
  // (e.g. LinkedIn Page messages are partner-gated). Show it, honestly, as N/A —
  // non-interactive — so an empty tab isn't mistaken for "we failed to load it".
  if (!dmCapable) {
    return (
      <li>
        <div
          title="This platform's API doesn't provide message-inbox access, so conversations can't be shown here."
          className="flex w-full cursor-default items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm text-ink-faint"
        >
          <span className="flex h-4 w-4 shrink-0 items-center justify-center text-ink-ghost" aria-hidden="true">
            {icon}
          </span>
          <span className="flex-1 truncate">{label}</span>
          <span className="shrink-0 text-[0.625rem] font-medium uppercase tracking-wide text-ink-ghost">
            N/A
          </span>
        </div>
        {/* N/A platforms have no inbox conversations, so accounts stay static (no filter). */}
        <ConnectionLines connections={connections} />
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'focus-ring flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors',
          active ? 'bg-tint font-medium text-tint-fg' : 'text-ink-label hover:bg-card',
        )}
      >
        <span
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center',
            active ? 'text-tint-fg' : 'text-ink-faint',
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
        <span className="flex-1 truncate">{label}</span>
        {count > 0 && <span className="text-xs text-ink-faint tabular-nums">{count}</span>}
      </button>
      <ConnectionLines
        connections={connections}
        platformKey={platformKey}
        selection={selection}
        accountTotals={accountTotals}
        onSelect={onSelect}
      />
    </li>
  );
}

/** The specific connected account(s) for a platform bucket — "handle · name" per discrete
 *  profile/page. Each is a button that scopes the inbox to just that account, so a platform with
 *  more than one connected account can be read one account at a time. */
function ConnectionLines({
  connections,
  platformKey,
  selection,
  accountTotals,
  onSelect,
}: {
  connections?: SocialConnection[];
  platformKey?: SocialPlatform;
  selection?: InboxSelection;
  accountTotals?: Record<string, number>;
  onSelect?: (selection: InboxSelection) => void;
}) {
  if (!connections || connections.length === 0) return null;
  return (
    <ul className="mb-1 ml-9 mt-0.5 space-y-0.5">
      {connections.map((c) => {
        const parts = [c.accountHandle, c.accountName]
          .map((s) => s?.trim())
          .filter((s): s is string => Boolean(s));
        const line = [...new Set(parts)].join(' · ') || 'Connected account';
        // Selectable only when we can route to it (id + platform + handler all present).
        if (!c.id || !platformKey || !onSelect) {
          return (
            <li
              key={c.id ?? c.accountName ?? line}
              className="truncate text-[0.6875rem] leading-tight text-ink-faint"
              title={line}
            >
              {line}
            </li>
          );
        }
        const active = selection?.kind === 'account' && selection.connectionId === c.id;
        const count = accountTotals?.[c.id] ?? 0;
        return (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelect({ kind: 'account', connectionId: c.id!, platform: platformKey })}
              aria-current={active ? 'true' : undefined}
              title={line}
              className={cn(
                'focus-ring flex w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-left text-[0.6875rem] leading-tight transition-colors',
                active
                  ? 'bg-tint font-medium text-tint-fg'
                  : 'text-ink-faint hover:bg-card hover:text-ink-label',
              )}
            >
              <span className="flex-1 truncate">{line}</span>
              {count > 0 && <span className="shrink-0 tabular-nums">{count}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ConversationList({
  conversations,
  selectedId,
  showAccount,
  onSelect,
}: {
  conversations: Conversation[];
  selectedId: string | null;
  showAccount: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex w-[21.25rem] shrink-0 flex-col border-r border-line-soft">
      <header className="flex shrink-0 items-center justify-between border-b border-line-soft px-4 py-3">
        <h2 className="text-sm font-semibold text-ink">Conversations</h2>
        <span className="text-xs text-ink-faint tabular-nums">{conversations.length}</span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <p className="px-4 py-6 text-sm text-ink-faint">No conversations in this view.</p>
        ) : (
          <ul>
            {conversations.map((c) => (
              <ConversationRow
                key={c.id}
                conversation={c}
                active={c.id === selectedId}
                showAccount={showAccount}
                onSelect={() => onSelect(c.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ConversationRow({
  conversation,
  active,
  showAccount,
  onSelect,
}: {
  conversation: Conversation;
  active: boolean;
  showAccount: boolean;
  onSelect: () => void;
}) {
  const meta = PLATFORM_META[conversation.platform];
  // Which of the user's accounts received this — shown as a chip when the view spans >1 account.
  const account = conversation.accountHandle || conversation.accountName;
  const last = conversation.messages[conversation.messages.length - 1];
  const snippet = last ? `${last.author === 'me' ? 'You: ' : ''}${last.text}` : '';

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'focus-ring flex w-full gap-3 border-b border-line-soft px-4 py-3 text-left transition-colors',
          active ? 'bg-tint' : 'hover:bg-hover',
        )}
      >
        <div className="relative shrink-0">
          <Avatar name={conversation.author.name} url={conversation.author.avatarUrl} size={40} />
          <span
            className={cn(
              'absolute -bottom-0.5 -right-0.5 flex h-[1.125rem] w-[1.125rem] items-center justify-center rounded-full ring-2 ring-card',
              meta.tile,
            )}
            aria-hidden="true"
          >
            <meta.Icon className="h-2.5 w-2.5" />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'truncate text-sm',
                conversation.unread ? 'font-semibold text-ink' : 'font-medium text-ink',
              )}
            >
              {conversation.author.name}
            </span>
            {last && (
              <span className="ml-auto shrink-0 text-xs text-ink-faint">
                {formatRelative(last.sentAt)}
              </span>
            )}
            {conversation.unread && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-action" aria-label="Unread" />
            )}
          </div>

          <p
            className={cn(
              'mt-0.5 truncate text-sm',
              conversation.unread ? 'text-ink-strong' : 'text-ink-muted',
            )}
          >
            {snippet}
          </p>

          <div className="mt-1.5 flex items-center gap-1.5">
            <Badge tone={TYPE_TONE[conversation.type]}>
              {CONVERSATION_TYPE_LABEL[conversation.type]}
            </Badge>
            {showAccount && account && (
              <span className="min-w-0 truncate text-[0.6875rem] text-ink-faint" title={account}>
                {account}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

/**
 * The open conversation: its header, its messages, and under them either the reply box or - for a
 * conversation nothing can be sent to - the reason why, in the reply box's place.
 */
export function ThreadPane({
  conversation,
  onSend,
  sending,
  sendError,
}: {
  conversation: Conversation | null;
  onSend: (text: string) => void;
  sending: boolean;
  sendError: string | null;
}) {
  if (!conversation) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-chip">
          <MessagesSquare className="h-6 w-6 text-ink-faint" aria-hidden="true" />
        </div>
        <p className="mt-3 text-sm font-medium text-ink-strong">
          Select a conversation to read and reply
        </p>
        <p className="mt-1 max-w-xs text-sm text-ink-faint">
          DMs from your connected social accounts land here.
        </p>
      </div>
    );
  }

  const meta = PLATFORM_META[conversation.platform];
  const replyBlocked = replyBlockedReason(conversation);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b border-line-soft px-5 py-3">
        <Avatar name={conversation.author.name} url={conversation.author.avatarUrl} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-semibold text-ink">
              {conversation.author.name}
            </h2>
            <span
              className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-md', meta.tile)}
              title={meta.label}
            >
              <meta.Icon className="h-3 w-3" />
            </span>
          </div>
          <p className="truncate text-sm text-ink-muted">
            {conversation.type === 'dm' ? (
              'Direct message'
            ) : conversation.onPost ? (
              <>
                {conversation.type === 'mention' ? 'mentioned you on' : 'commented on'}{' '}
                <span className="text-ink-label">“{conversation.onPost}”</span>
              </>
            ) : conversation.type === 'mention' ? (
              'Mentioned you'
            ) : (
              'Commented on your post'
            )}
          </p>
        </div>
        <Badge tone={TYPE_TONE[conversation.type]}>
          {CONVERSATION_TYPE_LABEL[conversation.type]}
        </Badge>
      </header>

      <ThreadMessages conversation={conversation} />

      {replyBlocked ? (
        // A conversation nothing can be sent to: say so where the reply box would be, so no reply
        // is typed and then lost.
        <div className="shrink-0 border-t border-line-soft px-5 py-3">
          <p className="text-sm text-ink-muted">{replyBlocked}</p>
        </div>
      ) : (
        <ReplyComposer
          onSend={onSend}
          authorName={conversation.author.name}
          sending={sending}
          error={sendError}
        />
      )}
    </div>
  );
}

function ThreadMessages({ conversation }: { conversation: Conversation }) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [conversation.id, conversation.messages.length]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        {conversation.messages.map((m) => (
          <MessageBubble key={m.id} message={m} authorName={conversation.author.name} />
        ))}
        <div ref={endRef} />
      </div>
    </div>
  );
}

function MessageBubble({ message, authorName }: { message: InboxMessage; authorName: string }) {
  const isMe = message.author === 'me';
  return (
    <div className={cn('flex flex-col gap-1', isMe ? 'items-end' : 'items-start')}>
      <span className="px-1 text-xs text-ink-faint">
        {isMe ? 'You' : authorName} · {formatRelative(message.sentAt)}
      </span>
      <div
        className={cn(
          'max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          isMe ? 'rounded-tr-sm bg-action text-action-fg' : 'rounded-tl-sm bg-page text-ink',
        )}
      >
        {message.text}
      </div>
    </div>
  );
}

function ReplyComposer({
  onSend,
  authorName,
  sending,
  error,
}: {
  onSend: (text: string) => void;
  authorName: string;
  sending: boolean;
  error: string | null;
}) {
  const [value, setValue] = useState('');

  const submit = () => {
    const text = value.trim();
    if (!text || sending) return;
    onSend(text);
    setValue('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const firstName = authorName.split(' ')[0];

  return (
    <div className="shrink-0 border-t border-line-soft px-5 py-3">
      {error && <p className="mb-2 text-sm text-red-600 dark:text-error-dark">{error}</p>}
      <div className="flex items-end gap-2">
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          aria-label={`Reply to ${authorName}`}
          placeholder={`Reply to ${firstName}…`}
          className="max-h-40 min-h-[2.5rem] flex-1 resize-none"
        />
        <button
          type="button"
          onClick={submit}
          disabled={value.trim() === '' || sending}
          aria-label="Send reply"
          className="focus-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-action text-action-fg transition-colors hover:bg-action-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <p className="mt-1.5 text-xs text-ink-faint">Press Enter to send, Shift+Enter for new line</p>
    </div>
  );
}
